import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TacticList } from "../tactic/TacticList";
import "./TeamSidePanel.css";

/**
 * Right panel: shows selected player info + tactic management.
 *
 * @param {{
 *   players: object[],
 *   selectedId: number | null,
 *   tactics: object[],
 *   tacticsLoading: boolean,
 *   onToggleStarter: (id: number) => void,
 *   onAssignTactic: (playerId: number, tacticId: number | null) => void,
 *   onCreate: (name: string) => Promise,
 *   onUpdate: (id: number, statements: object[]) => Promise,
 *   onDelete: (id: number) => Promise,
 * }} props
 */
export function TeamSidePanel({
  players, selectedId,
  tactics, tacticsLoading, tacticsError,
  onToggleStarter, onToggleGoalkeeper, onAssignTactic, onRenamePlayer,
  canCreate = true, onCreate, onUpdate, onDelete, onCreateBasic,
}) {
  const { t } = useTranslation();
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue]     = useState("");
  const nameInputRef = useRef(null);

  const player = players.find((p) => p.frontendId === selectedId) ?? null;

  // Reset name edit state when selection changes
  useEffect(() => {
    setEditingName(false);
    setNameValue(player?.name ?? "");
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingName) nameInputRef.current?.focus();
  }, [editingName]);

  const commitName = () => {
    onRenamePlayer(player.frontendId, nameValue.trim());
    setEditingName(false);
  };
  const hasStarter = players.some((p) => p.isStarter);
  const assignedTactic = player ? tactics.find((t) => t.id === player.tacticId) : null;

  return (
    <div className="team-side-panel">

      {/* Player section */}
      <div className="panel-player-section">
        {player ? (
          <>
            <div className="panel-player-title">
              {editingName ? (
                <input
                  ref={nameInputRef}
                  className="panel-name-input"
                  value={nameValue}
                  onChange={(e) => { setNameValue(e.target.value); onRenamePlayer(player.frontendId, e.target.value.trim()); }}
                  onBlur={commitName}
                  onKeyDown={(e) => { if (e.key === "Enter") commitName(); if (e.key === "Escape") setEditingName(false); }}
                  maxLength={30}
                  placeholder={t("team.namePlaceholder")}
                />
              ) : (
                <button className="panel-name-btn" onClick={() => { setNameValue(player.name); setEditingName(true); }}>
                  {player.name || <span className="panel-name-empty">{t("team.namePlaceholder")}</span>}
                  <span className="panel-name-pencil">✎</span>
                </button>
              )}
            </div>

            <div className="panel-player-meta">
              <span className={`panel-tactic-label ${!assignedTactic ? "panel-tactic-missing" : ""}`}>
                {assignedTactic ? assignedTactic.name : t("team.noTactic")}
              </span>
              {!assignedTactic && <span className="panel-warning">⚠</span>}
            </div>

            <button
              className={`panel-starter-btn ${player.isStarter ? "active" : ""}`}
              onClick={() => onToggleStarter(player.frontendId)}
            >
              {player.isStarter ? "★ " + t("team.starter") : "☆ " + t("team.setAsStarter")}
            </button>

            <button
              className={`panel-goalkeeper-btn ${player.isGoalkeeper ? "active" : ""}`}
              onClick={() => onToggleGoalkeeper(player.frontendId)}
            >
              {player.isGoalkeeper ? "🧤 " + t("team.goalkeeper") : "🧤 " + t("team.setAsGoalkeeper")}
            </button>

            {!hasStarter && !player.isStarter && (
              <p className="panel-hint">{t("team.noStarterWarning")}</p>
            )}
          </>
        ) : (
          <p className="panel-no-selection">{t("team.selectPlayer")}</p>
        )}
      </div>

      <div className="panel-divider" />

      {/* Tactic list — in assign mode when a player is selected */}
      <TacticList
        tactics={tactics}
        loading={tacticsLoading}
        error={tacticsError}
        canCreate={canCreate}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onCreateBasic={onCreateBasic}
        assignedId={player?.tacticId ?? null}
        onAssign={player ? (tacticId) => onAssignTactic(player.frontendId, tacticId) : null}
        defaultName={player?.name || ""}
        players={players}
      />
    </div>
  );
}
